import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import * as actions from '../../actions/generalActions';
import * as warehouseActions from '../../actions/warehouse';
import { RootState } from '../../reducers';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import Select from 'react-select';
import ReactToPrint from 'react-to-print';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaFileManager from '../../components/MikaFileManager/MikaFileManager';
import WarehouseComments from './WarehouseComments';
import { axiosCaller } from '../../utils/apiCaller';
import './WarehouseShipmentCard.scss';

interface Props {
    history: any;
    intl: any;
    warehouse: any;
    warehouseActions: any;
    actions: any;
    loading: boolean;
    permissions: any;
    warehouseProject: any;
    projectId: any;
    clientId: any;
    clientName: string;
    collapsed: boolean;
    projectIndex: number;
    onExpand: () => void;
}

const WarehouseShipmentCard: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage }),
        [intl]
    );

    const printRef = useRef<any>();
    const cardRef = useRef<HTMLDivElement>(null);
    const rightColRef = useRef<HTMLDivElement>(null);
    const [rightColHeight, setRightColHeight] = useState<number | null>(null);
    const [isCollapsed, setIsCollapsed] = useState(props.collapsed);
    const [quantities, setQuantities] = useState<Record<number, number>>({});
    const [transportAssignees, setTransportAssignees] = useState<any[]>([]);
    const [installationAssignees, setInstallationAssignees] = useState<any[]>([]);
    const [files, setFiles] = useState<any[]>([]);
    const [fileManagerKey, setFileManagerKey] = useState(0);
    const [comment, setComment] = useState('');
    const [label, setLabel] = useState(`${props.warehouseProject?.projectName || ''}`);
    const [withoutTransport, setWithoutTransport] = useState(false);
    const [withoutInstallation, setWithoutInstallation] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showRecords, setShowRecords] = useState(true);

    const [shipmentHistory, setShipmentHistory] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const products = props.warehouseProject?.products || [];
    const transportUsers = props.warehouse?.transportAssigneesOptions || [];
    const installationUsers = props.warehouse?.installationAssigneesOptions || [];

    useEffect(() => { setIsCollapsed(props.collapsed); }, [props.collapsed]);

    // Track right-column height so the left product table can cap at the same
    // height (with an internal scrollbar) instead of stretching the whole card.
    useEffect(() => {
        if (isCollapsed) return;
        const el = rightColRef.current;
        if (!el) return;
        const obs = new ResizeObserver(entries => {
            for (const entry of entries) setRightColHeight(entry.contentRect.height);
        });
        obs.observe(el);
        return () => obs.disconnect();
    }, [isCollapsed]);

    // Fetch shipment history independently via direct API call
    const fetchShipmentHistory = useCallback(() => {
        if (!props.projectId) return;
        setHistoryLoading(true);
        axiosCaller('GET', `warehouse-records/${props.projectId}/project`)
            .then(res => {
                const records = res?.data?.data || [];
                setShipmentHistory(Array.isArray(records) ? records : []);
            })
            .catch(() => {})
            .finally(() => setHistoryLoading(false));
    }, [props.projectId]);

    useEffect(() => {
        if (!isCollapsed && props.projectId) {
            fetchShipmentHistory();
        }
    }, [isCollapsed, props.projectId]);

    // Load editing record data
    useEffect(() => {
        const rec = props.warehouse?.warehouseRecord;
        if (rec?.id) {
            setIsEditing(true);
            setLabel(rec.label || '');
            setComment(rec.comment || '');
            setWithoutTransport(rec.withoutTransport === 1);
            setWithoutInstallation(rec.withoutInstallation === 1);
            setTransportAssignees((rec.transportUser || []).map((u: any) => ({ value: u.id, label: u.username })));
            setInstallationAssignees((rec.installationUsers || []).map((u: any) => ({ value: u.id, label: u.username })));
            const qtyMap: Record<number, number> = {};
            (rec.products || []).forEach((p: any) => { qtyMap[p.productId] = parseFloat(p.quantityForTransport) || 0; });
            setQuantities(qtyMap);
        }
    }, [props.warehouse?.warehouseRecord]);

    // Pre-fill quantities
    useEffect(() => {
        if (!isEditing && Object.keys(quantities).length === 0 && products.length > 0) {
            const prefilled: Record<number, number> = {};
            products.forEach((p: any) => {
                if (p.warehouseStatus === 'in_warehouse' && (p.availableInWarehouse ?? 0) > 0) {
                    prefilled[p.id] = p.availableInWarehouse;
                }
            });
            if (Object.keys(prefilled).length > 0) setQuantities(prefilled);
        }
    }, [products]);

    const resetForm = () => {
        setQuantities({});
        setTransportAssignees([]);
        setInstallationAssignees([]);
        setFiles([]);
        setFileManagerKey(prev => prev + 1);
        setComment('');
        setWithoutTransport(false);
        setWithoutInstallation(false);
        setLabel(`${props.warehouseProject?.projectName || ''} #${(shipmentHistory.length || 0) + 1}`);
        setIsEditing(false);
        props.warehouseActions.setWarehouseRecord({});
    };

    const handleSave = async () => {
        const hasProducts = Object.values(quantities).some(q => q > 0);
        if (!hasProducts) {
            toast.error(fm('warehouse.shipment.toast.selectProduct', 'Zgjidhni të paktën një produkt'));
            return;
        }

        setSaving(true);
        const formData = new FormData();
        formData.append('clientId', props.clientId);
        formData.append('projectId', String(props.projectId));
        formData.append('label', label);
        formData.append('comment', comment || '');
        formData.append('withoutTransport', withoutTransport ? '1' : '0');
        formData.append('withoutInstallation', withoutInstallation ? '1' : '0');

        if (!withoutTransport) transportAssignees.forEach(a => formData.append('transportUsers[]', a.value));
        if (!withoutInstallation) installationAssignees.forEach(a => formData.append('installationUsers[]', a.value));

        products.forEach((product: any) => {
            const qty = quantities[product.id] || 0;
            if (qty > 0) {
                formData.append('productIds[]', product.id);
                formData.append('quantity[]', String(qty));
            }
        });

        files.forEach(f => formData.append('invoice[]', f.file));

        try {
            if (isEditing && props.warehouse?.warehouseRecord?.id) {
                await props.warehouseActions.editWarehouseRecords(formData, props.warehouse.warehouseRecord.id);
            } else {
                await props.warehouseActions.createWarehouseRecords(formData);
            }
            await props.warehouseActions.fetchWarehouseClientInformation(props.clientId);
            resetForm();
            fetchShipmentHistory();
            cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = () => {
        if (isCollapsed) props.onExpand();
        setIsCollapsed(!isCollapsed);
    };

    const isAvailable = (row: any) => row.warehouseStatus === 'in_warehouse' && (row.availableInWarehouse ?? 0) > 0;

    const productColumns: MikaColumn[] = useMemo(() => [
        {
            key: 'designation', title: fm('production.board.productCol', 'Produkti'),
            render: (_: any, row: any) => (
                <div>
                    <span style={{ fontWeight: 500, color: isAvailable(row) ? '#1e293b' : '#94a3b8' }}>{row.designation?.name || '\u2014'}</span>
                    {row.type && <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 6 }}>({row.type})</span>}
                </div>
            )
        },
        {
            key: 'availableInWarehouse', title: fm('warehouse.status.inWarehouse', 'Në depo'),
            render: (val: any, row: any) => {
                if (!isAvailable(row)) return <span style={{ color: '#cbd5e1' }}>{'\u2014'}</span>;
                return <span style={{ fontWeight: 600, color: '#1e40af' }}>{Number(val) % 1 !== 0 ? Number(val).toFixed(2) : val}</span>;
            }
        },
        {
            key: '_qty', title: fm('warehouse.shipment.col.quantityToShip', 'Dërgo'), width: '120px',
            render: (_: any, row: any) => {
                if (!isAvailable(row) && !isEditing) return <span style={{ color: '#cbd5e1' }}>{'\u2014'}</span>;
                const available = row.availableInWarehouse ?? 0;
                const maxAvailable = isEditing ? available + parseFloat(props.warehouse?.warehouseRecord?.products?.find((p: any) => p.productId === row.id)?.quantityForTransport || 0) : available;
                return (
                    <input type="number" step="any" min={0} max={parseFloat(String(maxAvailable))}
                        value={quantities[row.id] ?? ''}
                        onChange={(e) => {
                            const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            setQuantities(prev => ({ ...prev, [row.id]: Math.min(val, parseFloat(String(maxAvailable))) }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="wsc-input"
                        style={{ width: 70, textAlign: 'center', border: (quantities[row.id] ?? 0) > 0 ? '2px solid #3b82f6' : undefined, background: (quantities[row.id] ?? 0) > 0 ? '#eff6ff' : undefined }}
                    />
                );
            }
        },
        { key: 'designation.unit', title: fm('warehouse.col.unit', 'Njësia'), hideOnMobile: true, render: (_: any, row: any) => row.designation?.unit?.name || '\u2014' },
        {
            key: 'warehouseStatus', title: fm('production.products.column.status', 'Statusi'), width: '100px',
            render: (_: any, row: any) => {
                const status = row.warehouseStatus;
                if (status === 'shipped') {
                    return <span className="wsc-status-badge wsc-status-shipped">{fm('warehouse.shipment.status.shipped', 'Dërguar')}</span>;
                }
                if (status === 'in_warehouse') {
                    return <span className="wsc-status-badge wsc-status-available">{fm('warehouse.status.inWarehouse', 'Në depo')}</span>;
                }
                // null/undefined = still in production
                return <span className="wsc-status-badge wsc-status-production">{fm('warehouse.shipment.status.production', 'Në prodhim')}</span>;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale, quantities, isEditing, props.warehouse?.warehouseRecord]);

    const productCount = products.filter((p: any) => p.warehouseStatus === 'in_warehouse' && (p.availableInWarehouse ?? 0) > 0).length;

    // Get files from editing record
    const existingFiles = props.warehouse?.warehouseRecord?.warehouseRecordFiles || [];

    return (
        <div className="wsc-card" ref={cardRef}>
            {/* Header */}
            <div className={`wsc-header ${!isCollapsed ? 'wsc-expanded' : ''}`} onClick={handleToggle}>
                <div className="wsc-header-left">
                    <span className="wsc-project-name">{props.warehouseProject?.projectName}</span>
                    <span className="wsc-product-count">{productCount} <FormattedMessage id="warehouse.shipment.productsLabel" defaultMessage="produkte" /></span>
                    {shipmentHistory.length > 0 && (
                        <span className="wsc-record-count">{shipmentHistory.length} <FormattedMessage id="warehouse.shipment.shipmentsLabel" defaultMessage="dërgesë" /></span>
                    )}
                </div>
                <div className="wsc-header-right">
                    {!isCollapsed && (
                        <ReactToPrint
                            trigger={() => (
                                <button className="wsc-print-btn" onClick={(e) => e.stopPropagation()} title={fm('warehouse.shipment.printDocument', 'Printo dokumentin')}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                                </button>
                            )}
                            content={() => printRef.current}
                        />
                    )}
                    <span className={`wsc-chevron ${!isCollapsed ? 'wsc-open' : ''}`}>{'\u25be'}</span>
                </div>
            </div>

            {/* Print (hidden) */}
            <div style={{ display: 'none' }}>
                <div ref={printRef} style={{ padding: 40, fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif' }}>
                    <h1 style={{ fontSize: 22, fontWeight: 700, borderBottom: '2px solid #1e293b', paddingBottom: 12, marginBottom: 24 }}>
                        {fm('warehouse.shipment.deliveryNote', 'Fletëdërgesë')}
                    </h1>
                    <div style={{ display: 'flex', gap: 40, marginBottom: 24 }}>
                        <div><div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>{fm('production.detail.client', 'Klienti')}</div><div style={{ fontSize: 15, fontWeight: 500 }}>{props.clientName}</div></div>
                        <div><div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>{fm('production.detail.project', 'Projekti')}</div><div style={{ fontSize: 15, fontWeight: 500 }}>{props.warehouseProject?.projectName}</div></div>
                        <div><div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>{fm('warehouse.shipment.date', 'Data')}</div><div style={{ fontSize: 15, fontWeight: 500 }}>{new Date().toLocaleDateString(intl.locale)}</div></div>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 32 }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#6b7280', borderBottom: '2px solid #e2e8f0' }}>{fm('production.board.productCol', 'Produkti')}</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#6b7280', borderBottom: '2px solid #e2e8f0' }}>{fm('production.detail.quantity', 'Sasia')}</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', color: '#6b7280', borderBottom: '2px solid #e2e8f0' }}>{fm('warehouse.col.unit', 'Njësia')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.filter((p: any) => (quantities[p.id] ?? 0) > 0).map((p: any) => (
                                <tr key={p.id}>
                                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{p.designation?.name} ({p.type})</td>
                                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{quantities[p.id]}</td>
                                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>{p.designation?.unit?.name || ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 60 }}>
                        <div><p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 40px' }}>{fm('warehouse.shipment.status.shipped', 'Dërguar')}</p><div style={{ borderTop: '1px solid #1e293b', paddingTop: 4, fontSize: 11, color: '#6b7280', width: 180 }}>{fm('warehouse.shipment.signature', 'Nënshkrimi')}</div></div>
                        <div><p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 40px' }}>{fm('warehouse.shipment.receivedLabel', 'Pranuar')}</p><div style={{ borderTop: '1px solid #1e293b', paddingTop: 4, fontSize: 11, color: '#6b7280', width: 180 }}>{fm('warehouse.shipment.signature', 'Nënshkrimi')}</div></div>
                    </div>
                </div>
            </div>

            {/* Body */}
            {!isCollapsed && (
                <div className="wsc-body">
                    {/* Two-column layout: products left, form right */}
                    <div className="wsc-columns">
                        {/* Left column — Product table.
                            CSS var `--wsc-col-max-h` drives .mika-table-wrapper's
                            max-height so the scrollbar lives inside the table box
                            and the sticky <th> anchors to the table's top. */}
                        <div className="wsc-col-left" style={rightColHeight ? ({ ['--wsc-col-max-h' as any]: `${rightColHeight}px` }) : undefined}>
                            <MikaTable className="wsc-col-left-table" columns={productColumns} data={products} loading={false} />
                        </div>

                        {/* Right column — Shipment form */}
                        <div className="wsc-col-right" ref={rightColRef}>
                            <div className="wsc-details">
                                <p className="wsc-details-title"><FormattedMessage id="warehouse.shipment.details" defaultMessage="Detajet e dërgesës" /></p>
                                <div className="wsc-details-body">

                                {/* Label */}
                                <div className="wsc-field" style={{ marginBottom: 8 }}>
                                    <label className="wsc-label"><FormattedMessage id="warehouse.shipment.label" defaultMessage="Emërtimi" /></label>
                                    <input type="text" className="wsc-input" value={label} onChange={(e) => setLabel(e.target.value)} />
                                </div>

                                {/* Description/note — always visible */}
                                <div className="wsc-field" style={{ marginBottom: 8 }}>
                                    <label className="wsc-label"><FormattedMessage id="warehouse.shipment.note" defaultMessage="Shënimi" /></label>
                                    <textarea className="wsc-textarea" value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                                        placeholder={fm('warehouse.shipment.notePlaceholder', 'Përshkrim i shkurtër i dërgesës...')} />
                                </div>

                                {/* Comment thread — visible when editing an existing record */}
                                {isEditing && props.warehouse?.warehouseRecord?.id && (
                                    <div className="wsc-comments-section">
                                        <label className="wsc-label" style={{ marginBottom: 4 }}><FormattedMessage id="warehouse.shipment.discussion" defaultMessage="Diskutimi" /></label>
                                        <WarehouseComments
                                            warehouseRecordId={props.warehouse.warehouseRecord.id}
                                            warehouseActions={props.warehouseActions}
                                        />
                                    </div>
                                )}

                                {/* Transport + Installation */}
                                <div className="wsc-assign-stack">
                                    <div className="wsc-assign-card">
                                        <div className="wsc-assign-header">
                                            <label className={`wsc-assign-check ${!withoutTransport ? 'wsc-checked' : ''}`}>
                                                <input type="checkbox" checked={!withoutTransport} onChange={() => setWithoutTransport(!withoutTransport)} />
                                                <FormattedMessage id="warehouse.shipment.needsTransport" defaultMessage="Kërkon transport" />
                                            </label>
                                        </div>
                                        {!withoutTransport ? (
                                            <div className="wsc-assign-select-wrap">
                                                <Select options={transportUsers} isMulti closeMenuOnSelect={false} value={transportAssignees}
                                                    onChange={(val: any) => setTransportAssignees(val || [])}
                                                    placeholder={fm('warehouse.shipment.selectAssignees', 'Zgjidhni...')}
                                                    className="react-select-container" classNamePrefix="react-select" />
                                            </div>
                                        ) : (
                                            <div className="wsc-assign-placeholder"><FormattedMessage id="warehouse.shipment.withoutTransport" defaultMessage="Pa transport" /></div>
                                        )}
                                    </div>
                                    <div className="wsc-assign-card">
                                        <div className="wsc-assign-header">
                                            <label className={`wsc-assign-check ${!withoutInstallation ? 'wsc-checked' : ''}`}>
                                                <input type="checkbox" checked={!withoutInstallation} onChange={() => setWithoutInstallation(!withoutInstallation)} />
                                                <FormattedMessage id="warehouse.shipment.needsInstallation" defaultMessage="Kërkon instalim" />
                                            </label>
                                        </div>
                                        {!withoutInstallation ? (
                                            <div className="wsc-assign-select-wrap">
                                                <Select options={installationUsers} isMulti closeMenuOnSelect={false} value={installationAssignees}
                                                    onChange={(val: any) => setInstallationAssignees(val || [])}
                                                    placeholder={fm('warehouse.shipment.selectAssignees', 'Zgjidhni...')}
                                                    className="react-select-container" classNamePrefix="react-select" />
                                            </div>
                                        ) : (
                                            <div className="wsc-assign-placeholder"><FormattedMessage id="warehouse.shipment.withoutInstallation" defaultMessage="Pa instalim" /></div>
                                        )}
                                    </div>
                                </div>

                                {/* Files */}
                                <div className="wsc-files-section">
                                    <MikaFileManager
                                        key={fileManagerKey}
                                        files={existingFiles.map((f: any) => ({
                                            id: f.id,
                                            name: f.fileName || 'file',
                                            url: f.fileUrl,
                                        }))}
                                        uploadSlots={[
                                            { key: 'invoice', label: fm('warehouse.shipment.deliveryNote', 'Fletëdërgesë'), icon: 'file', multiple: true },
                                        ]}
                                        onUpload={(key, uploadedFiles) => {
                                            setFiles(prev => [...prev, ...uploadedFiles.map(f => ({ file: f, fileName: f.name }))]);
                                        }}
                                    />
                                    {files.length > 0 && (
                                        <div className="wsc-pending-files">
                                            {files.map((f, i) => {
                                                const isImage = f.file?.type?.startsWith('image/');
                                                const previewUrl = isImage ? URL.createObjectURL(f.file) : null;
                                                const ext = f.fileName?.split('.').pop()?.toUpperCase() || '';
                                                return (
                                                    <div key={i} className={`wsc-pending-file ${isImage ? 'wsc-pending-file-image' : ''}`}>
                                                        {isImage ? (
                                                            <img src={previewUrl} alt={f.fileName} className="wsc-pending-thumb" />
                                                        ) : (
                                                            <span className="wsc-pending-ext">{ext}</span>
                                                        )}
                                                        <span className="wsc-pending-file-name">{f.fileName}</span>
                                                        <button className="wsc-pending-file-remove" type="button" onClick={() => { setFiles(prev => prev.filter((_, idx) => idx !== i)); setFileManagerKey(prev => prev + 1); }}
                                                            title={fm('warehouse.shipment.removeFile', 'Hiq')}>
                                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="wsc-actions">
                                    {isEditing && (
                                        <button className="wsc-btn-secondary" onClick={resetForm}>
                                            <FormattedMessage id="warehouse.shipment.clearForm" defaultMessage="Pastro formën" />
                                        </button>
                                    )}
                                    {(!props.permissions?.observer || props.permissions?.fullPrivileges) && (
                                        <button className="wsc-btn-primary" onClick={handleSave} disabled={saving}>
                                            {saving ? <FormattedMessage id="production.statusForm.saving" defaultMessage="Duke ruajtur..." />
                                                : isEditing ? <FormattedMessage id="warehouse.shipment.update" defaultMessage="Përditëso" />
                                                : <FormattedMessage id="warehouse.shipment.save" defaultMessage="Ruaj Dërgesën" />}
                                        </button>
                                    )}
                                </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Shipment history — self-contained, fetches its own data */}
                    <div className="wsc-history-section">
                        <div className="wsc-history-header" onClick={() => setShowRecords(!showRecords)}>
                            <span className="wsc-history-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                <FormattedMessage id="warehouse.shipment.history" defaultMessage="Historiku i dërgesave" />
                                {shipmentHistory.length > 0 && <span className="wsc-history-badge">{shipmentHistory.length}</span>}
                            </span>
                            <span className={`wsc-chevron ${showRecords ? 'wsc-open' : ''}`}>{'\u25be'}</span>
                        </div>
                        {showRecords && (
                            <div className="wsc-history-body">
                                {historyLoading ? (
                                    <div className="wsc-history-loading"><FormattedMessage id="production.board.loading" defaultMessage="Duke ngarkuar..." /></div>
                                ) : shipmentHistory.length === 0 ? (
                                    <div className="wsc-history-empty">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /></svg>
                                        <span><FormattedMessage id="warehouse.shipment.noHistory" defaultMessage="Asnjë dërgesë e mëparshme" /></span>
                                    </div>
                                ) : (
                                    <div className="wsc-history-list">
                                        {shipmentHistory.map((record: any, idx: number) => {
                                            const isSelected = props.warehouse?.warehouseRecord?.id === record?.id;
                                            const recordProducts = record?.productWarehouseRecord || [];
                                            return (
                                                <div key={record?.id || idx} className={`wsc-history-card ${isSelected ? 'wsc-history-selected' : ''}`}>
                                                    <div className="wsc-history-card-header">
                                                        <div className="wsc-history-card-info">
                                                            <span className="wsc-history-card-label">{record?.label || `#${idx + 1}`}</span>
                                                            <span className="wsc-history-card-date">{record?.createdAt || ''}</span>
                                                            {record?.createdBy && <span className="wsc-history-card-user">{record.createdBy}</span>}
                                                        </div>
                                                        <div className="wsc-history-card-actions">
                                                            {!isSelected ? (
                                                                <button className="wsc-record-action-btn wsc-edit" onClick={() => {
                                                                    props.warehouseActions.setWarehouseRecord({ ...record, products: recordProducts, projectIndex: props.projectIndex });
                                                                }} title={fm('common.action.edit', 'Ndrysho')}>
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                                </button>
                                                            ) : (
                                                                <button className="wsc-record-action-btn wsc-cancel" onClick={() => { resetForm(); }} title={fm('common.action.cancel', 'Anulo')}>
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                                                </button>
                                                            )}
                                                            <button className="wsc-record-action-btn wsc-delete" onClick={() => {
                                                                if (window.confirm(fm('common.confirm.title', 'A jeni të sigurt?'))) {
                                                                    props.warehouseActions.deleteWarehouseRecord(record?.id).then(() => {
                                                                        fetchShipmentHistory();
                                                                        props.warehouseActions.fetchWarehouseClientInformation(props.clientId);
                                                                    });
                                                                }
                                                            }} title={fm('common.action.delete', 'Fshi')}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="wsc-history-card-products">
                                                        {recordProducts.map((p: any, pidx: number) => (
                                                            <div key={pidx} className="wsc-history-product-row">
                                                                <span className="wsc-history-product-name">{p?.productName || p?.article_name || '\u2014'}</span>
                                                                <span className="wsc-history-product-qty">{p?.quantityForTransport || p?.quantity_for_transport || 0}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {record?.comment && (
                                                        <div className="wsc-history-card-comment">
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                                                            <span>{record.comment}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app, warehouse: state.warehouse };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        warehouseActions: bindActionCreators(warehouseActions as any, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(WarehouseShipmentCard as any));
